/**
 * Shared OCC lens selectors (S4.2) — pure, shared VERBATIM by the MSW
 * projection endpoints (`mocks/handlers/occ.ts`) and the S4.3 訂單控管中心
 * workbench UI, so every number on an OCC lens equals its source-store
 * number BY CONSTRUCTION (one record, many lenses — zero stored copies).
 *
 * Contract (holistic plan §S4.2, derive-never-restore):
 * - No I/O, no globals, no `Date.now()` — the caller supplies the rows and
 *   the `now` / `syncedAt` stamps, so handler and UI can never drift and
 *   vitest runs the exact logic the endpoints serve.
 * - 訂單異常 exceptions are a line×rule ENGINE, not a table: every row is
 *   re-derived from quantity/date/price facts on each read. There is no
 *   stored exception row and no manual close — resolving the source fact
 *   (replacement loaded, price released, supply confirmed) auto-clears the
 *   row because the rule simply stops firing.
 * - Referenced lenses (開票狀態 / 收貨狀態) project the finance / shipping
 *   stores read-only; every row carries `source` + `syncedAt` for the
 *   SourceStampTag + drill-through (OCC never edits).
 */

import type {
  FulfillmentExceptionReason,
  FulfillmentLineView,
  FulfillmentNodeCode,
  FulfillmentNodeConfig,
  FulfillmentOwnerRole,
} from '@/types/api/occ';
import type { ArInvoice, ArInvoiceStatus } from '@/types/api/finance';
import type {
  GoodsReceipt,
  GoodsReceiptStatus,
  ShipmentBatch,
  ShipmentBatchStatus,
} from '@/types/api/shipping';
import type { SalesOrderPriceStatus } from '@/types/api/salesOrder';
import { roundHalfUp2 } from '../pricing';

// ---------------------------------------------------------------------------
// Lens input row — the projected FulfillmentLine plus SO/product context
// ---------------------------------------------------------------------------

/**
 * The one row every OWNED lens reads: the derived `FulfillmentLineView`
 * (Order Balance 待出量 computed at read, never stored) merged with the SO /
 * product context the grid renders. `mocks/handlers/occ.ts`
 * `projectLensLines` builds these from the spine on every request.
 */
export interface OccLensLine extends FulfillmentLineView {
  soId: string;
  soNumber: string;
  customerName: string;
  sku: string;
  productName: string;
  vendorName: string | null;
}

// ---------------------------------------------------------------------------
// 訂單異常 exception engine — reason / severity / aging / owner
// ---------------------------------------------------------------------------

/**
 * Worklist reason enum (plan §S4.2): 未出貨回補 back order · 短裝 short ·
 * 溢裝 overflow · 延遲 delay · 價格覆核 price review. zh labels live in the
 * `occ` i18n namespace (S4.3) — never render these codes raw.
 */
export type OccExceptionReason =
  | 'back_order'
  | 'short_load'
  | 'overflow'
  | 'delay'
  | 'price_review';

export type OccExceptionSeverity = 'high' | 'medium' | 'low';

/** Aging buckets 0-2 / 3-7 / 8-14 / 15+ days (plan §S4.2). */
export const OCC_AGING_BUCKETS = ['0-2', '3-7', '8-14', '15+'] as const;
export type OccAgingBucket = (typeof OCC_AGING_BUCKETS)[number];

/**
 * A derived worklist row. `id` is deterministic (`<lineId>:<reason>`) so the
 * projection is stable across reads (React keys, acknowledge/snooze refs)
 * while remaining a pure function of the source facts — the row disappears
 * the moment its rule stops firing (auto-clear on resolve, no manual close).
 */
export interface OccExceptionRow {
  id: string;
  lineId: string;
  soId: string;
  soNumber: string;
  soLineId: string;
  internalProductId: string;
  sku: string;
  productName: string;
  customerName: string;
  reason: OccExceptionReason;
  severity: OccExceptionSeverity;
  /** Quantity at stake for this reason — the severity input. */
  qtyImpact: number;
  owner: FulfillmentOwnerRole;
  /** When the condition began — best deterministic fact available. */
  raisedAt: string;
  agingDays: number;
  agingBucket: OccAgingBucket;
  note: string | null;
  dueDate: string | null;
}

/**
 * Default owner per rule (owner enum, plan §S4.2; routing per §S3.2:
 * late→採購, short-ship→船務, price hold→財務). A line whose seam-stamped
 * exception maps to the same reason family keeps its stamped owner — the
 * owning module already routed it.
 */
export const EXCEPTION_RULE_OWNER: Record<OccExceptionReason, FulfillmentOwnerRole> = {
  back_order: 'procurement',
  short_load: 'shipping',
  overflow: 'shipping',
  delay: 'procurement',
  price_review: 'finance',
};

/**
 * Does a seam-stamped S3.3 reason belong to a derived rule's family?
 * `shortage` is ambiguous by design (pre-seal back-order vs at-seal short
 * load) so it matches both quantity-gap rules; `container_transfer` is a
 * delivery-time risk and folds into `delay`.
 */
export function spineReasonMatches(
  spine: FulfillmentExceptionReason,
  rule: OccExceptionReason
): boolean {
  switch (spine) {
    case 'shortage':
      return rule === 'short_load' || rule === 'back_order';
    case 'over_qty':
      return rule === 'overflow';
    case 'delay':
    case 'container_transfer':
      return rule === 'delay';
  }
}

/**
 * Severity FROM IMPACT (plan §S4.2):
 * - `price_review` blocks fulfillment AND invoicing → always high;
 * - `overflow` means the container facts are wrong → always high;
 * - otherwise by impact ratio of the ordered quantity:
 *   ≥50% high · ≥10% medium · else low.
 */
export function deriveExceptionSeverity(
  reason: OccExceptionReason,
  qtyImpact: number,
  qtyOrdered: number
): OccExceptionSeverity {
  if (reason === 'price_review' || reason === 'overflow') return 'high';
  const ratio = qtyOrdered > 0 ? qtyImpact / qtyOrdered : 1;
  if (ratio >= 0.5) return 'high';
  if (ratio >= 0.1) return 'medium';
  return 'low';
}

/** Whole days between the raise stamp and `now`, floored, never negative. */
export function agingDaysOf(raisedAt: string, now: string): number {
  const elapsed = Date.parse(now) - Date.parse(raisedAt);
  if (!Number.isFinite(elapsed) || elapsed <= 0) return 0;
  return Math.floor(elapsed / 86_400_000);
}

export function agingBucketOf(days: number): OccAgingBucket {
  if (days <= 2) return '0-2';
  if (days <= 7) return '3-7';
  if (days <= 14) return '8-14';
  return '15+';
}

export interface OccExceptionEngineInput {
  /** The read stamp — aging is computed against this, never `Date.now()`. */
  now: string;
  /**
   * SO price-deviation state by soId (S2.1 `pending_approval` blocks
   * fulfillment/invoicing) — feed with `indexPriceStatusBySo`.
   */
  priceStatusBySoId?: Record<string, SalesOrderPriceStatus>;
}

/** Index helper shared by the handler and the UI (one lookup, both sides). */
export function indexPriceStatusBySo(
  salesOrders: ReadonlyArray<{ id: string; priceStatus: SalesOrderPriceStatus }>
): Record<string, SalesOrderPriceStatus> {
  return Object.fromEntries(salesOrders.map((so) => [so.id, so.priceStatus]));
}

/**
 * The line×rule engine. Each rule fires from FACTS on the derived view —
 * quantities, dates, price status — never from a stored exception row:
 *
 * - R1 back_order  未出貨回補 — pre-seal supply gap: the vendor confirmed
 *                  less than ordered (A1 60 of 100), or a seam-stamped
 *                  shortage with no confirmed supply yet (missing vendor).
 * - R2 short_load  短裝 — a seal happened and left Order Balance 待出量 > 0
 *                  (loaded quantity is fixed at the seal; the gap is real).
 * - R3 overflow    溢裝 — negative Order Balance (over-load, preserved never
 *                  clamped) or receipt above the ordered quantity.
 * - R4 delay       延遲 — the seam stamped a delay-family exception, or the
 *                  owner's due date passed with open work remaining. NOT
 *                  fired for overdue quantity-gap stamps (their own row
 *                  already carries the age — no double counting).
 * - R5 price_review 價格覆核 — the linked SO sits in `pending_approval`
 *                  (deviation hold): fulfillment and invoicing are blocked.
 *
 * Resolving the source fact auto-clears the row: load the replacement and R2
 * stops firing; release the price and R5 stops firing. There is no close
 * endpoint anywhere.
 */
export function deriveLineExceptions(
  line: OccLensLine,
  input: OccExceptionEngineInput
): OccExceptionRow[] {
  const rows: OccExceptionRow[] = [];
  const stamped = line.exception ?? null;

  const push = (reason: OccExceptionReason, qtyImpact: number, raisedAt: string) => {
    const stampMatches = stamped !== null && spineReasonMatches(stamped.reason, reason);
    const agingDays = agingDaysOf(raisedAt, input.now);
    rows.push({
      id: `${line.id}:${reason}`,
      lineId: line.id,
      soId: line.soId,
      soNumber: line.soNumber,
      soLineId: line.soLineId,
      internalProductId: line.internalProductId,
      sku: line.sku,
      productName: line.productName,
      customerName: line.customerName,
      reason,
      severity: deriveExceptionSeverity(reason, qtyImpact, line.qtyOrdered),
      qtyImpact,
      owner: stampMatches ? line.owner.role : EXCEPTION_RULE_OWNER[reason],
      raisedAt,
      agingDays,
      agingBucket: agingBucketOf(agingDays),
      note: stampMatches ? (stamped.note ?? null) : null,
      dueDate: line.owner.dueDate ?? null,
    });
  };

  // R1 back_order — pre-seal only; after a seal the gap belongs to R2.
  const confirmedShort = line.qtyConfirmed < line.qtyOrdered;
  const supplyConfirmedShort = line.poLineIds.length > 0 && line.qtyConfirmed > 0;
  const stampedShortagePreSeal = stamped !== null && stamped.reason === 'shortage';
  if (line.qtyLoaded === 0 && confirmedShort && (supplyConfirmedShort || stampedShortagePreSeal)) {
    push('back_order', line.qtyOrdered - line.qtyConfirmed, line.updatedAt);
  }

  // R2 short_load — sealed short; remainder still open on the load axis.
  if (line.qtyLoaded > 0 && line.orderBalance > 0) {
    push('short_load', line.orderBalance, line.updatedAt);
  }

  // R3 overflow — signed truth of the load axis, negative preserved.
  const overBy = Math.max(
    line.orderBalance < 0 ? -line.orderBalance : 0,
    line.qtyReceived - line.qtyOrdered
  );
  if (overBy > 0) {
    push('overflow', overBy, line.updatedAt);
  }

  // R4 delay — stamped delay family, or overdue due date with open work on a
  // line whose stamp (if any) is itself delay-family (no double counting).
  const openWork = line.qtyOpenToLoad > 0 || line.qtyOpenToReceive > 0;
  const stampedDelay = stamped !== null && spineReasonMatches(stamped.reason, 'delay');
  const dueDate = line.owner.dueDate;
  const overdue =
    dueDate !== null &&
    dueDate !== undefined &&
    Date.parse(dueDate) < Date.parse(input.now) &&
    openWork &&
    (stamped === null || stampedDelay);
  if (stampedDelay || overdue) {
    const raisedAt = !stampedDelay && overdue && dueDate ? dueDate : line.updatedAt;
    push('delay', Math.max(0, line.qtyOrdered - line.qtyReceived), raisedAt);
  }

  // R5 price_review — the deviation hold blocks the whole line.
  if (input.priceStatusBySoId?.[line.soId] === 'pending_approval') {
    push('price_review', line.qtyOrdered, line.updatedAt);
  }

  return rows;
}

const SEVERITY_RANK: Record<OccExceptionSeverity, number> = { high: 0, medium: 1, low: 2 };

/** Worklist order (S4.3): severity first, then oldest (largest age) first. */
export function sortExceptionWorklist(rows: OccExceptionRow[]): OccExceptionRow[] {
  return [...rows].sort(
    (a, b) =>
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
      b.agingDays - a.agingDays ||
      a.id.localeCompare(b.id)
  );
}

/** Run the engine over every line and return the sorted worklist. */
export function selectExceptionRows(
  lines: OccLensLine[],
  input: OccExceptionEngineInput
): OccExceptionRow[] {
  return sortExceptionWorklist(lines.flatMap((line) => deriveLineExceptions(line, input)));
}

export interface OccExceptionSummary {
  total: number;
  bySeverity: Record<OccExceptionSeverity, number>;
  byReason: Record<OccExceptionReason, number>;
  byAgingBucket: Record<OccAgingBucket, number>;
}

/** Counts for the stat card + segmented category chips (S4.3). */
export function selectExceptionSummary(rows: OccExceptionRow[]): OccExceptionSummary {
  const summary: OccExceptionSummary = {
    total: rows.length,
    bySeverity: { high: 0, medium: 0, low: 0 },
    byReason: { back_order: 0, short_load: 0, overflow: 0, delay: 0, price_review: 0 },
    byAgingBucket: { '0-2': 0, '3-7': 0, '8-14': 0, '15+': 0 },
  };
  rows.forEach((row) => {
    summary.bySeverity[row.severity] += 1;
    summary.byReason[row.reason] += 1;
    summary.byAgingBucket[row.agingBucket] += 1;
  });
  return summary;
}

// ---------------------------------------------------------------------------
// Board roll-up — stat numbers + per-SO 訂單進度總覽 rows
// ---------------------------------------------------------------------------

/**
 * THE Order Balance 待出量 work total — Σ `qtyOpenToLoad` (per-line floored
 * work-queue axis). The W11 未出貨 Back-Order report MUST use this same
 * selector so OCC and the report can never disagree.
 */
export function selectOrderBalanceTotal(
  lines: ReadonlyArray<Pick<FulfillmentLineView, 'qtyOpenToLoad'>>
): number {
  return lines.reduce((sum, line) => sum + line.qtyOpenToLoad, 0);
}

/** 待裝櫃 — lines with open work on the load axis. */
export function selectLinesToLoadCount(
  lines: ReadonlyArray<Pick<FulfillmentLineView, 'qtyOpenToLoad'>>
): number {
  return lines.filter((line) => line.qtyOpenToLoad > 0).length;
}

// ---------------------------------------------------------------------------
// Product-scoped lens variants (SR2.2) — the `?productId=` deep-link
// ---------------------------------------------------------------------------

/**
 * Product-scoped lens variant (SR2.2): filter projected lens rows to ONE
 * product by its canonical `internalProductId` — the primary Ship identity and
 * the `?productId=` deep-link value the 產品管控中心 quintet emits (SR3.5).
 * Generic over any row carrying `internalProductId` (both `OccLensLine` and the
 * derived `OccExceptionRow`), so the OCC lenses, the Back-Order report, and the
 * control-center deep links all scope through the SAME predicate — never a
 * per-surface fork. A blank id is the unscoped case and returns every row.
 */
export function filterLinesByProduct<T extends { internalProductId: string }>(
  rows: readonly T[],
  internalProductId: string | null | undefined
): T[] {
  if (!internalProductId) return [...rows];
  return rows.filter((row) => row.internalProductId === internalProductId);
}

/**
 * THE product-scoped 待出量 total — Σ `qtyOpenToLoad` over ONE product's lines,
 * routed through the single `selectOrderBalanceTotal` balance source (never a
 * second sum). Equals the Back-Order report's per-product figure BY
 * CONSTRUCTION, exactly as the board total equals the BO grand total (§SR2.2
 * accept: BO report product total === OCC 待出量 for that product).
 */
export function selectOrderBalanceTotalForProduct(
  lines: ReadonlyArray<Pick<FulfillmentLineView, 'qtyOpenToLoad'> & { internalProductId: string }>,
  internalProductId: string
): number {
  return selectOrderBalanceTotal(filterLinesByProduct(lines, internalProductId));
}

export interface OccBoardStats {
  openExceptions: { total: number; bySeverity: Record<OccExceptionSeverity, number> };
  /** 待出量 total — `selectOrderBalanceTotal`. */
  orderBalanceTotal: number;
  /** 待裝櫃 line count. */
  linesToLoad: number;
  /** 最久未處理 — age in days of the oldest open exception, null when clean. */
  oldestOpenExceptionDays: number | null;
  nodeCounts: Record<FulfillmentNodeCode, number>;
}

/** The 4 stat-card numbers + 進度點 distribution, all from shared selectors. */
export function selectBoardStats(
  lines: OccLensLine[],
  exceptions: OccExceptionRow[]
): OccBoardStats {
  const nodeCounts: Record<FulfillmentNodeCode, number> = {
    vendor_held: 0,
    loader_staged: 0,
    on_water: 0,
    customer_received: 0,
  };
  lines.forEach((line) => {
    nodeCounts[line.node] += 1;
  });
  const summary = selectExceptionSummary(exceptions);
  return {
    openExceptions: { total: summary.total, bySeverity: summary.bySeverity },
    orderBalanceTotal: selectOrderBalanceTotal(lines),
    linesToLoad: selectLinesToLoadCount(lines),
    oldestOpenExceptionDays:
      exceptions.length === 0 ? null : Math.max(...exceptions.map((row) => row.agingDays)),
    nodeCounts,
  };
}

/** 訂單進度總覽 row — one per SO, rolled up from its FulfillmentLines. */
export interface OccOrderRollupRow {
  soId: string;
  soNumber: string;
  customerName: string;
  lineCount: number;
  qtyOrdered: number;
  qtyLoaded: number;
  qtyReceived: number;
  /** % received of ordered, rounded — may exceed 100 on over-receipt (truth). */
  receivedPct: number;
  /** Signed Σ Order Balance 待出量 across the SO's lines. */
  orderBalance: number;
  /** Distinct 進度點 present, in config sort order — chips render from config labels. */
  nodes: FulfillmentNodeCode[];
  exceptionCount: number;
  maxSeverity: OccExceptionSeverity | null;
  updatedAt: string;
}

export function selectOrderRollups(
  lines: OccLensLine[],
  exceptions: OccExceptionRow[],
  nodeConfigs: FulfillmentNodeConfig[]
): OccOrderRollupRow[] {
  const nodeOrder = new Map(nodeConfigs.map((config) => [config.code, config.sortOrder]));
  const bySo = new Map<string, OccLensLine[]>();
  lines.forEach((line) => {
    const group = bySo.get(line.soId);
    if (group) group.push(line);
    else bySo.set(line.soId, [line]);
  });
  return [...bySo.entries()]
    .map(([soId, group]) => {
      const qtyOrdered = group.reduce((sum, line) => sum + line.qtyOrdered, 0);
      const qtyReceived = group.reduce((sum, line) => sum + line.qtyReceived, 0);
      const soExceptions = exceptions.filter((row) => row.soId === soId);
      const maxSeverity =
        soExceptions.length === 0
          ? null
          : soExceptions.reduce<OccExceptionSeverity>(
              (worst, row) => (SEVERITY_RANK[row.severity] < SEVERITY_RANK[worst] ? row.severity : worst),
              'low'
            );
      return {
        soId,
        soNumber: group[0].soNumber,
        customerName: group[0].customerName,
        lineCount: group.length,
        qtyOrdered,
        qtyLoaded: group.reduce((sum, line) => sum + line.qtyLoaded, 0),
        qtyReceived,
        receivedPct: qtyOrdered > 0 ? Math.round((qtyReceived / qtyOrdered) * 100) : 0,
        orderBalance: group.reduce((sum, line) => sum + line.orderBalance, 0),
        nodes: [...new Set(group.map((line) => line.node))].sort(
          (a, b) => (nodeOrder.get(a) ?? 99) - (nodeOrder.get(b) ?? 99)
        ),
        exceptionCount: soExceptions.length,
        maxSeverity,
        updatedAt: group.reduce(
          (latest, line) => (line.updatedAt > latest ? line.updatedAt : latest),
          group[0].updatedAt
        ),
      };
    })
    .sort((a, b) => a.soNumber.localeCompare(b.soNumber));
}

// ---------------------------------------------------------------------------
// 裝櫃批次 batch lens (owned; tab hidden until W6 seeds batches)
// ---------------------------------------------------------------------------

export interface OccBatchLensRow {
  batchId: string;
  batchNumber: string;
  status: ShipmentBatchStatus;
  containerNumber: string | null;
  sealNumber: string | null;
  etd: string;
  eta: string;
  cutOffAt: string | null;
  departedAt: string | null;
  poIds: string[];
  lineCount: number;
  qtyPlanned: number;
  qtyLoaded: number;
  /** % loaded of planned, rounded — the live utilization number. */
  loadedPct: number;
  cbmTotal: number;
  /** Lines sealed below plan — only meaningful at/after cut-off. */
  shortLoadedLines: number;
}

/**
 * Project the shipping store's batches — the lens numbers ARE the source
 * numbers because both sides sum the same `ShipmentBatchLine` facts.
 */
export function selectBatchLensRows(batches: ShipmentBatch[]): OccBatchLensRow[] {
  return batches.map((batch) => {
    const qtyPlanned = batch.lines.reduce((sum, line) => sum + line.qtyPlanned, 0);
    const qtyLoaded = batch.lines.reduce((sum, line) => sum + line.qtyLoaded, 0);
    const sealed = batch.status !== 'open';
    return {
      batchId: batch.id,
      batchNumber: batch.batchNumber,
      status: batch.status,
      containerNumber: batch.containerNumber ?? null,
      sealNumber: batch.sealNumber ?? null,
      etd: batch.etd,
      eta: batch.eta,
      cutOffAt: batch.cutOffAt ?? null,
      departedAt: batch.departedAt ?? null,
      poIds: [...batch.poIds],
      lineCount: batch.lines.length,
      qtyPlanned,
      qtyLoaded,
      loadedPct: qtyPlanned > 0 ? Math.round((qtyLoaded / qtyPlanned) * 100) : 0,
      cbmTotal: roundHalfUp2(batch.lines.reduce((sum, line) => sum + line.cbm, 0)),
      shortLoadedLines: sealed
        ? batch.lines.filter((line) => line.qtyLoaded < line.qtyPlanned).length
        : 0,
    };
  });
}

// ---------------------------------------------------------------------------
// Referenced lenses — read-only projections with SourceStampTag facts
// ---------------------------------------------------------------------------

export type OccLensSource = 'finance' | 'shipping';

/** 開票狀態 row — projected from the finance store, 來源:會計. */
export interface OccInvoiceStatusRow {
  invoiceId: string;
  invoiceNumber: string;
  customerId: number;
  customerName: string;
  soId: string | null;
  soNumber: string | null;
  amount: number;
  paidAmount: number;
  outstanding: number;
  currency: string;
  status: ArInvoiceStatus;
  dueDate: string;
  source: 'finance';
  syncedAt: string;
}

export function selectInvoiceStatusRows(
  invoices: ArInvoice[],
  syncedAt: string
): OccInvoiceStatusRow[] {
  return invoices.map((invoice) => ({
    invoiceId: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    customerId: invoice.customerId,
    customerName: invoice.customerName,
    soId: invoice.soId ?? null,
    soNumber: invoice.soNumber ?? null,
    amount: invoice.amount,
    paidAmount: invoice.paidAmount,
    outstanding: roundHalfUp2(invoice.amount - invoice.paidAmount),
    currency: invoice.currency,
    status: invoice.status,
    dueDate: invoice.dueDate,
    source: 'finance',
    syncedAt,
  }));
}

/** 收貨狀態 row — projected from the shipping store, 來源:船務. */
export interface OccReceiptStatusRow {
  receiptId: string;
  grNumber: string;
  poId: string;
  poNumber: string;
  status: GoodsReceiptStatus;
  receivedAt: string | null;
  lineCount: number;
  qtyReceived: number;
  qtyDamaged: number;
  source: 'shipping';
  syncedAt: string;
}

export function selectReceiptStatusRows(
  receipts: GoodsReceipt[],
  syncedAt: string
): OccReceiptStatusRow[] {
  return receipts.map((receipt) => ({
    receiptId: receipt.id,
    grNumber: receipt.grNumber,
    poId: receipt.poId,
    poNumber: receipt.poNumber,
    status: receipt.status,
    receivedAt: receipt.receivedAt ?? null,
    lineCount: receipt.lines.length,
    qtyReceived: receipt.lines.reduce((sum, line) => sum + line.qtyReceived, 0),
    qtyDamaged: receipt.lines.reduce((sum, line) => sum + line.qtyDamaged, 0),
    source: 'shipping',
    syncedAt,
  }));
}
